import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getLocalApiUrl, authFetch } from '../../config/api'

const COLUMNS = [
  { id: 'assigned', label: 'Assigned' },
  { id: 'sent', label: 'Sent' },
  { id: 'opened', label: 'Opened' },
  { id: 'in_progress', label: 'In progress' },
  { id: 'submitted', label: 'Submitted' },
  { id: 'in_review', label: 'In review' },
  { id: 'overdue', label: 'Overdue' },
  { id: 'accepted', label: 'Accepted' },
  { id: 'cancelled', label: 'Cancelled' },
]

export default function FormsPipeline() {
  const navigate = useNavigate()
  const [rows, setRows] = useState([])
  const [error, setError] = useState('')
  const [facing, setFacing] = useState('')

  const load = useCallback(async () => {
    setError('')
    try {
      const qs = facing ? `?facing=${encodeURIComponent(facing)}` : ''
      const res = await authFetch(getLocalApiUrl(`/api/forms/assignments${qs}`))
      const j = await res.json()
      if (!res.ok || !j.success) throw new Error(j.error || 'Failed to load pipeline')
      setRows(j.data || [])
    } catch (e) {
      setError(e.message)
    }
  }, [facing])

  useEffect(() => { load() }, [load])

  const byStatus = useMemo(() => {
    const map = Object.fromEntries(COLUMNS.map((c) => [c.id, []]))
    for (const row of rows) {
      const key = map[row.status] ? row.status : 'assigned'
      map[key].push(row)
    }
    return map
  }, [rows])

  return (
    <div className="p-6">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Forms pipeline</h1>
          <p className="text-sm text-slate-500 mt-1">Every in-flight assignment across published forms.</p>
        </div>
        <select className="rounded-lg border border-slate-300 px-3 py-2 text-sm" value={facing} onChange={(e) => setFacing(e.target.value)}>
          <option value="">All facings</option>
          <option value="internal">Internal</option>
          <option value="patron">Patron</option>
        </select>
      </div>
      {error ? <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div> : null}
      <div className="flex gap-3 overflow-x-auto pb-4">
        {COLUMNS.map((col) => (
          <div key={col.id} className="w-64 shrink-0 rounded-xl border border-slate-200 bg-slate-50 p-2">
            <div className="flex items-center justify-between px-1 py-1.5">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-600">{col.label}</div>
              <div className="text-xs text-slate-400">{byStatus[col.id].length}</div>
            </div>
            <div className="space-y-2">
              {byStatus[col.id].map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => navigate(`/compliance-safe/forms/assignments/${a.id}`)}
                  className="w-full text-left rounded-lg border border-slate-200 bg-white p-3 hover:border-teal-400"
                >
                  <div className="text-sm font-semibold text-slate-900 truncate">
                    {a.formName}
                    {a.isTest ? <span className="ml-1 text-[10px] font-semibold uppercase text-amber-700">Test</span> : null}
                  </div>
                  <div className="text-xs text-slate-500 mt-1 truncate">{a.subjectLabel || a.subjectType}</div>
                  <div className="text-xs text-slate-500 mt-1 truncate">
                    {a.assigneeUserName || a.assigneeRoleLabel || (a.facing === 'patron' ? 'Patron' : 'Unassigned')}
                  </div>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
